import { type ComputedRef, type Ref, computed, nextTick, onMounted, ref } from 'vue';
import {
  type MaybeComputedElementRef,
  tryOnScopeDispose,
  unrefElement,
  useResizeObserver,
} from '@vueuse/core';

/**
 * Order in which the candidate header gives things up as space runs out. Each level keeps
 * everything the previous one gave up. Once the name would be squeezed below `MIN_NAME_WIDTH_PX`
 * the score badge and the origin chip wrap onto a second line, where they start out expanded and
 * then collapse the same way — the second line itself never wraps again.
 */
export const HeaderCollapseLevel = {
  None: 0,
  JobTitle: 1,
  OriginChip: 2,
  InterviewLabel: 3,
  Wrap: 4,
  WrapHideJobTitle: 5,
  WrapIconOnly: 6,
} as const;

export const MAX_HEADER_COLLAPSE_LEVEL = HeaderCollapseLevel.WrapIconOnly;

/** Last level where everything still shares one line. */
export const MAX_SINGLE_LINE_COLLAPSE_LEVEL = HeaderCollapseLevel.InterviewLabel;

/** Free space a level has to regain, on top of what it costs, before it is restored. */
export const HEADER_COLLAPSE_HYSTERESIS_PX = 20;

/**
 * Narrowest the candidate name is allowed to get before the badge and chip wrap to a second line —
 * roughly nine or ten characters of the name's `font-serif text-lg font-black` (18px) face. Tunable.
 */
export const MIN_NAME_WIDTH_PX = 99;

/**
 * Slop allowed when checking a level's width against the one it learned, before the difference is
 * read as the content having changed. Every quantity the row is measured from — `offsetLeft`,
 * `offsetWidth`, `clientWidth` — is a rounded integer, and the actions container's own width is
 * text-driven and fractional, so a level's width can land on either of two adjacent integers as
 * the row's fractional width sweeps past it, with nothing changed underneath. Real content changes
 * are tens of pixels: the score badge and its job title arrive well after the row is first
 * measured. Reading sub-pixel geometry instead would not help, because `scrollWidth` — which the
 * name's overflow is measured from — has no fractional counterpart to pair it with.
 */
const CONTENT_CHANGE_TOLERANCE_PX = 2;

/**
 * Safety valve for the settling walk below. The ladder moves one level per measurement and relearns
 * its widths at most once per walk, so a walk that settles cannot legitimately be longer than twice
 * the ladder; the cap is only there so a pathological geometry cannot spin inside a frame.
 */
const MAX_SETTLE_STEPS = 2 * (MAX_HEADER_COLLAPSE_LEVEL + 1);

export interface HeaderCollapseInput {
  /** Levels currently engaged. */
  level: number;
  /** Free space left in the row; negative means the name is already truncating. */
  slack: number;
  /** Width the row's content needs at each level, indexed by level. */
  requiredWidthByLevel: readonly (number | undefined)[];
  /** Width the name currently has to work with. */
  nameWidth?: number;
  /** Width the name would get back on a single line, used to decide un-wrapping. */
  singleLineNameWidth?: number;
  /** Free space left on the second line, once the badge and chip live there. */
  wrappedSlack?: number;
  /** Width the second line needs at each wrapped level, indexed by level. */
  wrappedRequiredWidthByLevel?: readonly (number | undefined)[];
  minNameWidth?: number;
  hysteresis?: number;
}

/**
 * Whether a level measuring differently from the width it learned means the content changed, as
 * opposed to the row's integer geometry having rounded the other way. A level with nothing learned
 * yet has nothing to disagree with.
 */
function hasContentChanged(learnedWidth: number | undefined, measuredWidth: number): boolean {
  return (
    learnedWidth !== undefined &&
    Math.abs(learnedWidth - measuredWidth) > CONTENT_CHANGE_TOLERANCE_PX
  );
}

/** Steps back one level when the width that level saved, plus the buffer, is free again. */
function restoredLevel(
  level: number,
  slack: number,
  requiredWidthByLevel: readonly (number | undefined)[],
  hysteresis: number,
): number {
  const collapsedWidth = requiredWidthByLevel[level];
  const expandedWidth = requiredWidthByLevel[level - 1];
  if (collapsedWidth === undefined || expandedWidth === undefined) {
    return level;
  }

  return slack >= expandedWidth - collapsedWidth + hysteresis ? level - 1 : level;
}

/**
 * Picks the collapse level for the width the row currently has. Collapsing happens one level per
 * measurement so each step is driven by real numbers, and a level is only restored when the space
 * it costs plus the hysteresis buffer is available, which is what keeps the boundary from
 * flickering.
 */
export function nextHeaderCollapseLevel({
  level,
  slack,
  requiredWidthByLevel,
  nameWidth,
  singleLineNameWidth,
  wrappedSlack,
  wrappedRequiredWidthByLevel = [],
  minNameWidth = MIN_NAME_WIDTH_PX,
  hysteresis = HEADER_COLLAPSE_HYSTERESIS_PX,
}: HeaderCollapseInput): number {
  if (level >= HeaderCollapseLevel.Wrap) {
    // The second line has its own ladder: give up the job title, then the chip's label.
    if (wrappedSlack !== undefined && wrappedSlack < 0) {
      return Math.min(level + 1, MAX_HEADER_COLLAPSE_LEVEL);
    }

    if (level > HeaderCollapseLevel.Wrap) {
      return wrappedSlack === undefined || wrappedSlack === 0
        ? level
        : restoredLevel(level, wrappedSlack, wrappedRequiredWidthByLevel, hysteresis);
    }

    if (singleLineNameWidth === undefined) {
      return level;
    }

    return singleLineNameWidth >= minNameWidth + hysteresis
      ? MAX_SINGLE_LINE_COLLAPSE_LEVEL
      : level;
  }

  if (slack < 0) {
    if (level === MAX_SINGLE_LINE_COLLAPSE_LEVEL) {
      return nameWidth !== undefined && nameWidth < minNameWidth ? HeaderCollapseLevel.Wrap : level;
    }

    return level + 1;
  }

  if (slack === 0 || level === HeaderCollapseLevel.None) {
    return level;
  }

  return restoredLevel(level, slack, requiredWidthByLevel, hysteresis);
}

export interface HeaderCollapseTargets {
  /** The header row itself. */
  row: MaybeComputedElementRef;
  /** The candidate name — the only element allowed to shrink. */
  name: MaybeComputedElementRef;
  /** Score badge, observed because its width changes when the score and job title load. */
  scoreBadge: MaybeComputedElementRef;
  /** Candidate-type chip, the last element before the row's free space. */
  originChip: MaybeComputedElementRef;
  /** Actions container, pushed to the right edge by `ml-auto`. */
  actions: MaybeComputedElementRef;
}

export interface HeaderCollapseState {
  hideJobTitle: ComputedRef<boolean>;
  isOriginChipIconOnly: ComputedRef<boolean>;
  shortInterviewLabel: ComputedRef<boolean>;
  /** Score badge and origin chip have moved to a second line. */
  isWrapped: ComputedRef<boolean>;
  isNameTruncated: Ref<boolean>;
  remeasure: () => void;
  reset: () => void;
}

function htmlElement(target: MaybeComputedElementRef): HTMLElement | undefined {
  const element = unrefElement(target);
  return element instanceof HTMLElement ? element : undefined;
}

/**
 * Collapses the candidate header based on what the row actually measures rather than on the
 * viewport width, so it reacts to anything that changes the space available: window resizes,
 * the sidebar opening, zoom, and long names or job titles.
 */
export function useHeaderCollapse(targets: HeaderCollapseTargets): HeaderCollapseState {
  const level = ref<number>(HeaderCollapseLevel.None);
  const isNameTruncated = ref(false);

  // Learned as the row visits each level, so restoring a level can be priced from measured
  // numbers instead of guessing, and never from the mutation that is currently in flight.
  const requiredWidthByLevel: (number | undefined)[] = new Array(
    MAX_HEADER_COLLAPSE_LEVEL + 1,
  ).fill(undefined);

  // Same widths without the name, which is what prices the name's share of the row and so decides
  // both wrapping and un-wrapping.
  const nonNameWidthByLevel: (number | undefined)[] = new Array(MAX_HEADER_COLLAPSE_LEVEL + 1).fill(
    undefined,
  );

  // Width the second line needs at each of its own levels, learned the same way.
  const wrappedRequiredWidthByLevel: (number | undefined)[] = new Array(
    MAX_HEADER_COLLAPSE_LEVEL + 1,
  ).fill(undefined);

  let settling = false;
  let disposed = false;

  /** Whether the level moved, which is what tells the walk below it is not settled yet. */
  function measure(): boolean {
    const row = htmlElement(targets.row);
    const name = htmlElement(targets.name);
    const scoreBadge = htmlElement(targets.scoreBadge);
    const originChip = htmlElement(targets.originChip);
    const actions = htmlElement(targets.actions);
    if (!row || !name || !scoreBadge || !originChip || !actions) {
      return false;
    }

    const isWrapped = level.value >= HeaderCollapseLevel.Wrap;

    // `ml-auto` on the actions container swallows every spare pixel, so the gap in front of it is
    // the row's free space. Once that gap is gone the name starts truncating instead. Wrapped, the
    // badge and chip sit on the second line, which leaves the name in front of that gap.
    const lastItemBeforeGap = isWrapped ? name : originChip;
    const freeSpace =
      actions.offsetLeft - (lastItemBeforeGap.offsetLeft + lastItemBeforeGap.offsetWidth);
    const nameWidth = name.clientWidth;
    const nameOverflow = Math.max(0, name.scrollWidth - nameWidth);
    const slack = freeSpace - nameOverflow;

    isNameTruncated.value = nameOverflow > 0;
    const rowWidth = row.clientWidth;
    const requiredWidth = rowWidth - slack;

    // A level's width prices what the header holds at that level, and at a fixed level it does not
    // depend on how wide the row is. So the same level measuring differently means the content
    // changed underneath the ladder — the score and its job title land well after the row was first
    // measured. Every other level was priced against the old content, and a step back reads the
    // level below the current one, so the whole set has to be walked and learned again.
    let contentChanged: boolean;

    let wrappedSlack: number | undefined;
    if (isWrapped) {
      // Wrapped, the name has `flex-1` and swallows the gap, so there is no free space left to read
      // and `requiredWidth` is only the row's own width — it says nothing about the content. What
      // the second line needs is the number worth learning here, and since neither the badge nor
      // the chip shrinks, their laid-out extent is exactly that, even while it overflows the row.
      const wrappedWidth = originChip.offsetLeft + originChip.offsetWidth - scoreBadge.offsetLeft;
      const learnedWidth = wrappedRequiredWidthByLevel[level.value];
      contentChanged = hasContentChanged(learnedWidth, wrappedWidth);
      wrappedRequiredWidthByLevel[level.value] = wrappedWidth;
      wrappedSlack = rowWidth - wrappedWidth;
    } else {
      const learnedWidth = requiredWidthByLevel[level.value];
      contentChanged = hasContentChanged(learnedWidth, requiredWidth);
      requiredWidthByLevel[level.value] = requiredWidth;
      nonNameWidthByLevel[level.value] = requiredWidth - name.scrollWidth;
    }

    // From the top of the ladder there is nothing stale left behind, and every level the walk down
    // visits is re-learned on the way, so the numbers are current again by the time they are read.
    if (contentChanged && level.value !== HeaderCollapseLevel.None) {
      forgetLearnedWidths();
      level.value = HeaderCollapseLevel.None;
      return true;
    }

    const singleLineNonNameWidth = nonNameWidthByLevel[MAX_SINGLE_LINE_COLLAPSE_LEVEL];

    const previousLevel = level.value;
    level.value = nextHeaderCollapseLevel({
      level: level.value,
      slack,
      requiredWidthByLevel,
      nameWidth,
      singleLineNameWidth:
        singleLineNonNameWidth === undefined ? undefined : rowWidth - singleLineNonNameWidth,
      wrappedSlack,
      wrappedRequiredWidthByLevel,
    });

    return level.value !== previousLevel;
  }

  /**
   * The ladder takes one level per measurement, and a level landing can change positions without
   * changing any observed element's size — which a ResizeObserver never reports — so the walk has
   * to keep itself going. It steps on Vue's own tick rather than on an animation frame: the level
   * reaches the DOM on the same microtask checkpoint that queued it, so the whole walk runs and is
   * measured inside the frame that started it and only the level it settles on is ever painted.
   * Stepping a frame at a time instead showed every level on the way, which is what made candidate
   * navigation flash the header open and re-collapse it.
   */
  async function settle() {
    if (settling) {
      return;
    }

    settling = true;
    for (let step = 0; step < MAX_SETTLE_STEPS; step += 1) {
      await nextTick();
      if (disposed || !measure()) {
        break;
      }
    }
    settling = false;
  }

  function remeasure() {
    if (measure()) {
      void settle();
    }
  }

  function forgetLearnedWidths() {
    requiredWidthByLevel.fill(undefined);
    nonNameWidthByLevel.fill(undefined);
    wrappedRequiredWidthByLevel.fill(undefined);
  }

  function reset() {
    forgetLearnedWidths();
    level.value = HeaderCollapseLevel.None;
    isNameTruncated.value = false;
    void settle();
  }

  tryOnScopeDispose(() => {
    disposed = true;
  });

  useResizeObserver(
    [targets.row, targets.name, targets.scoreBadge, targets.originChip, targets.actions],
    remeasure,
  );

  onMounted(async () => {
    await nextTick();
    remeasure();
  });

  const isWrapped = computed(() => level.value >= HeaderCollapseLevel.Wrap);

  return {
    // The second line starts out with the whole row, so the badge and chip render expanded there
    // again and then collapse against the second line's own levels.
    hideJobTitle: computed(() =>
      isWrapped.value
        ? level.value >= HeaderCollapseLevel.WrapHideJobTitle
        : level.value >= HeaderCollapseLevel.JobTitle,
    ),
    isOriginChipIconOnly: computed(() =>
      isWrapped.value
        ? level.value >= HeaderCollapseLevel.WrapIconOnly
        : level.value >= HeaderCollapseLevel.OriginChip,
    ),
    shortInterviewLabel: computed(() => level.value >= HeaderCollapseLevel.InterviewLabel),
    isWrapped,
    isNameTruncated,
    remeasure,
    reset,
  };
}
